import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Zoom a posun obsahu — prst, kolečko i tlačítka.
 *
 * Bydlelo to uvnitř `SmernicPreviewModal`, protože to vzniklo kvůli směrnicím
 * na telefonu. Jenže „přiblížit si, co je na papíře" není věc směrnic, je to
 * věc každého skenu — u dokladů z toho člověk luští částky a variabilní
 * symboly. Než to psát podruhé, stěhuje se to sem.
 *
 * Gesta: dva prsty zoom, jeden prst při přiblížení posun, dvojité ťuknutí
 * přepíná 1× ↔ 2,5×, kolečko s Ctrl/Cmd zoomuje. Při 1× se svislý scroll
 * pouští ven (`touchAction: pan-y`), aby šlo listovat stránkou pod tím.
 *
 * `resetKey` vrátí zoom na 1× při změně (jiná stránka, jiný soubor).
 * `onScaleChange` hlásí ven aktuální měřítko — volající si podle něj může
 * překreslit obsah v odpovídajícím rozlišení, protože CSS transform sám
 * jen roztáhne pixely, které už má (viz `DokladNahledModal`).
 */
// ── Pinch-zoom + pan wrapper (touch + wheel) ──
// Dva prsty = zoom, jeden prst při přiblížení = posun, double-tap = přepnout 1x ↔ 2.5x
export default function ZoomPanViewer({
    children,
    maxZoom = 5,
    resetKey,
    onScaleChange,
    /** `'ctrl'` = kolečko zoomuje jen s Ctrl/Cmd, `'vzdy'` = kolečko zoomuje vždy. */
    kolecko = 'ctrl',
}) {
    const containerRef = useRef(null);
    const [scale, setScale] = useState(1);
    const [tx, setTx] = useState(0);
    const [ty, setTy] = useState(0);
    const [tahne, setTahne] = useState(false);

    const gesture = useRef({
        mode: null, // 'pinch' | 'pan' | null
        startDist: 0,
        startScale: 1,
        startTx: 0,
        startTy: 0,
        startMidX: 0,
        startMidY: 0,
        panStartX: 0,
        panStartY: 0,
        lastTap: 0,
    });

    useEffect(() => {
        setScale(1); setTx(0); setTy(0);
    }, [resetKey]);

    // Měřítko ven jedním místem — nastavuje se na čtyřech (gesto, kolečko,
    // tlačítka, reset) a hlásit ho z každého zvlášť je cesta k tomu, že na
    // jedno se zapomene.
    useEffect(() => {
        onScaleChange?.(scale);
    }, [scale, onScaleChange]);

    const clampPan = useCallback((nextScale, nextTx, nextTy) => {
        const el = containerRef.current;
        if (!el) return { tx: nextTx, ty: nextTy };
        const rect = el.getBoundingClientRect();
        const maxX = Math.max(0, (rect.width * (nextScale - 1)) / 2);
        const maxY = Math.max(0, (rect.height * (nextScale - 1)) / 2);
        return {
            tx: Math.max(-maxX, Math.min(maxX, nextTx)),
            ty: Math.max(-maxY, Math.min(maxY, nextTy)),
        };
    }, []);

    const applyScale = useCallback((nextScale, anchorX, anchorY) => {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const ax = anchorX - rect.left - rect.width / 2;
        const ay = anchorY - rect.top - rect.height / 2;
        const ratio = nextScale / scale;
        const newTx = ax - (ax - tx) * ratio;
        const newTy = ay - (ay - ty) * ratio;
        const clamped = clampPan(nextScale, newTx, newTy);
        setScale(nextScale);
        setTx(clamped.tx);
        setTy(clamped.ty);
    }, [scale, tx, ty, clampPan]);

    const reset = useCallback(() => {
        setScale(1); setTx(0); setTy(0);
    }, []);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const onTouchStart = (e) => {
            if (e.touches.length === 2) {
                const [a, b] = [e.touches[0], e.touches[1]];
                gesture.current.mode = 'pinch';
                gesture.current.startDist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
                gesture.current.startScale = scale;
                gesture.current.startTx = tx;
                gesture.current.startTy = ty;
                gesture.current.startMidX = (a.clientX + b.clientX) / 2;
                gesture.current.startMidY = (a.clientY + b.clientY) / 2;
                e.preventDefault();
            } else if (e.touches.length === 1) {
                const now = Date.now();
                const t = e.touches[0];
                if (now - gesture.current.lastTap < 300) {
                    if (scale > 1.05) {
                        reset();
                    } else {
                        applyScale(2.5, t.clientX, t.clientY);
                    }
                    gesture.current.lastTap = 0;
                    gesture.current.mode = null;
                    e.preventDefault();
                    return;
                }
                gesture.current.lastTap = now;
                if (scale > 1.01) {
                    gesture.current.mode = 'pan';
                    gesture.current.panStartX = t.clientX;
                    gesture.current.panStartY = t.clientY;
                    gesture.current.startTx = tx;
                    gesture.current.startTy = ty;
                    e.preventDefault();
                }
            }
        };

        const onTouchMove = (e) => {
            if (gesture.current.mode === 'pinch' && e.touches.length >= 2) {
                const [a, b] = [e.touches[0], e.touches[1]];
                const dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
                const rawScale = gesture.current.startScale * (dist / gesture.current.startDist);
                const nextScale = Math.max(1, Math.min(maxZoom, rawScale));
                const midX = (a.clientX + b.clientX) / 2;
                const midY = (a.clientY + b.clientY) / 2;
                const rect = el.getBoundingClientRect();
                const ax = gesture.current.startMidX - rect.left - rect.width / 2;
                const ay = gesture.current.startMidY - rect.top - rect.height / 2;
                const ratio = nextScale / gesture.current.startScale;
                let newTx = ax - (ax - gesture.current.startTx) * ratio + (midX - gesture.current.startMidX);
                let newTy = ay - (ay - gesture.current.startTy) * ratio + (midY - gesture.current.startMidY);
                const clamped = clampPan(nextScale, newTx, newTy);
                setScale(nextScale);
                setTx(clamped.tx);
                setTy(clamped.ty);
                e.preventDefault();
            } else if (gesture.current.mode === 'pan' && e.touches.length === 1) {
                const t = e.touches[0];
                const newTx = gesture.current.startTx + (t.clientX - gesture.current.panStartX);
                const newTy = gesture.current.startTy + (t.clientY - gesture.current.panStartY);
                const clamped = clampPan(scale, newTx, newTy);
                setTx(clamped.tx);
                setTy(clamped.ty);
                e.preventDefault();
            }
        };

        const onTouchEnd = (e) => {
            if (e.touches.length === 0) {
                gesture.current.mode = null;
            } else if (e.touches.length === 1 && gesture.current.mode === 'pinch') {
                gesture.current.mode = 'pan';
                gesture.current.panStartX = e.touches[0].clientX;
                gesture.current.panStartY = e.touches[0].clientY;
                gesture.current.startTx = tx;
                gesture.current.startTy = ty;
            }
        };

        const onWheel = (e) => {
            // `kolecko='ctrl'` je pro obsah, pod kterým se ještě scrolluje
            // (směrnice). Tam, kde se scrollovat nedá a kolečko nemá jinou
            // práci, je `'vzdy'` — nutit Ctrl u prohlížeče dokumentu je otrava.
            if (kolecko !== 'vzdy' && !e.ctrlKey && !e.metaKey) return;
            e.preventDefault();
            const factor = Math.exp(-e.deltaY * 0.0015);
            const nextScale = Math.max(1, Math.min(maxZoom, scale * factor));
            applyScale(nextScale, e.clientX, e.clientY);
        };

        // ── Myš ──
        //
        // Tohle tu dlouho chybělo úplně: komponenta vznikla pro směrnice na
        // telefonu, takže uměla jen prsty. Na desktopu se pak přiblížený
        // dokument nedal posunout vůbec a zoom tlačítky mířil pořád doprostřed
        // — vypadalo to, že zoom „umí jen střed". Neuměl nic jiného, protože
        // se s ním nedalo pohnout.
        const naStisk = (e) => {
            if (e.button !== 0 || scale <= 1.01) return;
            e.preventDefault();
            gesture.current.mode = 'mys';
            gesture.current.panStartX = e.clientX;
            gesture.current.panStartY = e.clientY;
            gesture.current.startTx = tx;
            gesture.current.startTy = ty;
            setTahne(true);
        };

        // Na `window`, ne na prvku: kdo při tahu vyjede kurzorem ven, nesmí
        // zůstat dokument viset v půlce pohybu.
        const naPohyb = (e) => {
            if (gesture.current.mode !== 'mys') return;
            e.preventDefault();
            const clamped = clampPan(
                scale,
                gesture.current.startTx + (e.clientX - gesture.current.panStartX),
                gesture.current.startTy + (e.clientY - gesture.current.panStartY),
            );
            setTx(clamped.tx);
            setTy(clamped.ty);
        };

        const naPusteni = () => {
            if (gesture.current.mode !== 'mys') return;
            gesture.current.mode = null;
            setTahne(false);
        };

        // Dvojklik zrcadlí dvojité ťuknutí — přepíná 1× ↔ 2,5× tam, kam se míří.
        const naDvojklik = (e) => {
            e.preventDefault();
            if (scale > 1.05) reset();
            else applyScale(Math.min(maxZoom, 2.5), e.clientX, e.clientY);
        };

        el.addEventListener('touchstart', onTouchStart, { passive: false });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        el.addEventListener('touchend', onTouchEnd);
        el.addEventListener('touchcancel', onTouchEnd);
        el.addEventListener('wheel', onWheel, { passive: false });
        el.addEventListener('mousedown', naStisk);
        el.addEventListener('dblclick', naDvojklik);
        window.addEventListener('mousemove', naPohyb);
        window.addEventListener('mouseup', naPusteni);
        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
            el.removeEventListener('touchcancel', onTouchEnd);
            el.removeEventListener('wheel', onWheel);
            el.removeEventListener('mousedown', naStisk);
            el.removeEventListener('dblclick', naDvojklik);
            window.removeEventListener('mousemove', naPohyb);
            window.removeEventListener('mouseup', naPusteni);
        };
    }, [scale, tx, ty, maxZoom, kolecko, applyScale, clampPan, reset]);

    const zoomIn = () => {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        applyScale(Math.min(maxZoom, scale * 1.5), rect.left + rect.width / 2, rect.top + rect.height / 2);
    };
    const zoomOut = () => {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        applyScale(Math.max(1, scale / 1.5), rect.left + rect.width / 2, rect.top + rect.height / 2);
    };

    return (
        <div ref={containerRef} style={{
            position: 'relative', width: '100%', height: '100%',
            overflow: 'hidden',
            // Při zoomu 1x pustíme svislý scroll (pro víc stránek), jinak si pan řešíme sami
            touchAction: scale > 1.01 ? 'none' : 'pan-y',
            userSelect: 'none',
            // Ruka říká, že se s tím dá hýbat, dřív než to člověk zkusí.
            cursor: scale > 1.01 ? (tahne ? 'grabbing' : 'grab') : 'default',
        }}>
            <div style={{
                width: '100%', height: '100%',
                transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
                transformOrigin: 'center center',
                transition: gesture.current.mode ? 'none' : 'transform 0.15s ease-out',
                willChange: 'transform',
            }}>
                {children}
            </div>
            <div style={{
                position: 'absolute', right: 8, bottom: 8,
                display: 'flex', flexDirection: 'column', gap: 6,
                pointerEvents: 'auto', zIndex: 2,
            }}>
                <button type="button" onClick={zoomIn} aria-label="Přiblížit" style={zoomBtnStyle}>+</button>
                <button type="button" onClick={zoomOut} aria-label="Oddálit" style={{ ...zoomBtnStyle, opacity: scale <= 1.01 ? 0.4 : 1 }}>−</button>
                {scale > 1.01 && (
                    <button type="button" onClick={reset} aria-label="Reset zoomu" style={{ ...zoomBtnStyle, fontSize: 11 }}>1:1</button>
                )}
            </div>
        </div>
    );
}

const zoomBtnStyle = {
    width: 36, height: 36, borderRadius: 7,
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,0,0,0.55)', color: '#fff',
    fontSize: 18, fontWeight: 600, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
};
